import { ComponentProps, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import locale from "@bonliva-crm/locale";
import { MultiCollapsible } from "@bonliva-crm/ui";
import { useUpdateEffect } from "@hooks";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { NoCustomerGroupOption } from "@customers/CustomerSearch/utils";
import { store } from "@store";
import { CustomerType } from "@graphql";
import { isEmpty } from "lodash";
import { ActionMeta } from "react-select5";

export type CustomerGroupsFilterProps = {
  label?: string;
} & FilterWithFavouriteProps;

export const CustomerGroupsFilter = ({ label, ...props }: CustomerGroupsFilterProps) => {
  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const [collapsedGroups, setCollapsedGroups] = useState<CustomerType[]>([
    CustomerType.Hospital
  ]);
  const title = label ?? locale["Customer type"];

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const onCollapse = (option: any) => {
    const value = option.value;
    if (collapsedGroups.some((x) => x === value)) {
      setCollapsedGroups([...collapsedGroups.filter((x) => x !== value)]);
    } else {
      collapsedGroups.push(value as CustomerType);
      setCollapsedGroups([...collapsedGroups]);
    }
  };

  const clear = () => resetField(props.name);

  const onChange = (value: LabelValue[], actionMeta: ActionMeta<LabelValue>) => {
    setValue(props.name, value, { shouldDirty: true });
  };
  const onClear = () => {
    setValue(props.name, null, { shouldDirty: true });
  };

  const inputOptions: LabelValue[] = [];

  const groups = [
    NoCustomerGroupOption,
    ...store().cache.customerGroups.map((g) => ({
      label: g,
      value: g,
      parent: CustomerType.Hospital
    }))
  ];

  // For correct order
  inputOptions.push({
    label: locale.customerType[CustomerType.Region],
    value: CustomerType.Region
  });
  inputOptions.push({
    label: locale.customerType[CustomerType.Hospital],
    // options: groups,
    value: CustomerType.Hospital,
    children: groups.map((x) => x.value)
  });
  inputOptions.push(...groups);
  inputOptions.push({
    label: locale.customerType[CustomerType.Department],
    value: CustomerType.Department
  });

  const commonProps: ComponentProps<typeof MultiCollapsible<any>> = {
    placeholder: title,
    options: inputOptions,
    name: props.name,
    value: field.value,
    onChange,
    isClearable: !isEmpty(field.value),
    isCollapsible: (option) => option.children !== undefined,
    isCollapsed: (option) => collapsedGroups.some((x) => x === option.value),
    isOptionSelected: (option) => {
      return field?.value
        ? field.value.some((x) => x.value === option.value || x.value === option.parent)
        : false;
    },
    isOptionDisabled: (option) => {
      return field?.value ? field.value.some((x) => x.value === option.parent) : false;
    },
    filterOption: (option, inputValue) => {
      if (collapsedGroups.some((x) => x === option?.data.parent)) {
        return false;
      } else {
        return true;
      }
    },
    onCollapse,
    maxMenuHeight: 600,
    controlShouldRenderValue: false,
    hideSelectedOptions: false,
    // showOptionDivider: false,
    // showDropDownIndicator: true,
    isMulti: true,
    closeMenuOnSelect: false,
    showBadge: !isEmpty(field.value),
    // sortOptionsOnMenuOpenFn: (options) =>
    //   sortSelectedFirst({
    //     options,
    //     value: field.value,
    //     valueOptionEqualityFn: (value, option) => value.value === option.value
    //   }),
    onClear
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiCollapsible<any>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          inputLabel={""}
          menuPortalTarget={document.body}
          controlShouldRenderValue
          openMenuOnFocus
          placeholder={locale.SelectValue}
          showBadge={false}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <div style={{ minWidth: 140 }}>
      <MultiCollapsible
        {...commonProps}
        styleProps={{
          control: { showBorder: true },
          menu: { showBorder: true }
        }}
      />
    </div>
  );
};
